import { AppSettingType, Co2MaterialLibrary, Co2MaterialType } from "@api/apiClient";
import { DensityType } from "@api/gql/graphql";
import { useAllMaterials } from "@api/gql/materials";
import DataGrid from "@components/DataGrid/DataGrid";
import { GridToolbarCompareButton } from "@components/DataGrid/components/toolbar/GridToolbarCompareButton";
import { useMaterialColumns } from "@components/MaterialSelectDialog/MaterialSelectGrid.columns";
import { transformMetadataToLocationOptions, transformMetadataToSubSystemOptions, transformMetadataToSystemOptions } from "@features/analysis/utils/metadataEntityUtils";
import useAppStore from "@hooks/useAppStore";
import useCompareMaterialsStore from "@hooks/useCompareMaterialsStore";
import { useMitt } from "@hooks/useMitt";
import i18n from "@i18n/i18n";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { alpha, Box, Checkbox, darken, lighten, Theme, Typography, useTheme } from "@mui/material";
import {
  getGridDefaultColumnTypes,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterOperator,
  GridRowSelectionModel,
  GridSlotsComponentsProps,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import { MaterialTabType } from "@/types/MaterialTabs";
import _ from "lodash";
import { observer } from "mobx-react";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import useState from "react-usestateref";

type ExtendedGridColDef = {
  hidden?: boolean;
  export?: boolean;
} & GridColDef;

export interface MaterialGridProps {
  height?: any;
  co2MaterialLibrary: Co2MaterialLibrary;
  searchText: string;
  metrics?: {
    length: { total: number };
    area: { total: number };
    volume: { total: number };
    quantity: { total: number };
  };
  compare?: boolean;
  compareSelectModel?: any;
  onRowSelectModelChange?: (co2MaterialLibrary: Co2MaterialLibrary, co2MaterialType: Co2MaterialType) => void;
  onCompareSelectModelChange?: (id: string, checked: boolean) => void;
  onShowCompare?: () => void;
  tabType: MaterialTabType;
  showTabs: boolean;
}

export const MaterialSelectGrid = observer(
  forwardRef((props: MaterialGridProps, ref: any) => {
    console.log("MaterialSelectGrid", props);
    const appStore = useAppStore();
    const compareMaterialsStore = useCompareMaterialsStore();
    const { getTabColumns, getDefaultColumns } = useMaterialColumns();
    const { emitter } = useMitt();
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>({});
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [paginationModel, setPaginationModel, paginationModelRef] = useState({ pageSize: 200, page: 0 });
    const [rowCount, setRowCount] = useState(0);
    const [sortModel, setSortModel, sortModelRef] = useState<GridSortModel>([
      { field: "name", sort: "asc" },
      { field: "category", sort: "asc" },
      { field: "a1A2A3", sort: "asc" },
    ]);
    const [pageSizeOptions, setPageSizeOptions] = useState([10, 25, 50, 100]);
    const [restoreInitialState, setRestoreInitialState] = useState(true);
    const apiRef = useGridApiRef();
    const localStorageKey = `matSelDlg-${props.co2MaterialLibrary.co2MaterialLibraryId}`;

    useImperativeHandle(ref, () => ({
      selectMaterial: (co2MaterialType: Co2MaterialType | null | undefined) => {
        const row = rows.find((r) => {
          if (co2MaterialType?.co2MaterialTypeId !== null) {
            return r.co2MaterialTypeId === co2MaterialType?.co2MaterialTypeId;
          }
          if (co2MaterialType?.externalId !== null) {
            return r.externalId === co2MaterialType?.externalId;
          }
          return false;
        });
        console.log(`selectMaterial: `, props.co2MaterialLibrary.name, row);
        if (row) {
          apiRef.current.selectRows([row.id], true, true);
        } else {
          apiRef.current.selectRows([], false, true);
        }
      },
    }));

    useEffect(() => {
      const current = apiRef.current;
      const handleClose = async () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { filter, ...state } = current.exportState();
        const currentState = {
          ...state,
          sorting: { ...state.sorting, sortModel: sortModelRef.current },
          pagination: { ...state.pagination, paginationModel: paginationModelRef.current },
        };
        localStorage.setItem(localStorageKey, JSON.stringify(currentState));
      };

      emitter.on("close", handleClose); // TODO: create event type

      return () => {
        emitter.off("close", handleClose);
      };
    }, [apiRef]);

    useEffect(() => {
      const handleResetSelection = async (event: { co2MaterialLibrary: Co2MaterialLibrary; co2MaterialType: Co2MaterialType }) => {
        // Reset the selection for the other child grids
        if (event.co2MaterialLibrary.co2MaterialLibraryId !== props.co2MaterialLibrary.co2MaterialLibraryId) {
          console.log("Resetting selection for: ", props.co2MaterialLibrary.name);
          if (co2MaterialTypes === undefined) {
            setRowSelectionModel([]);
          }
        }
      };

      emitter.on("resetSelection", handleResetSelection); // TODO: create event type

      return () => {
        emitter.off("resetSelection", handleResetSelection);
      };
    }, []);

    // TODO: replace this with the new useAutosizeColumns hook
    useEffect(() => {
      const handleColumnVisibilityModelChange = (params: GridColumnVisibilityModel) => {
        const newColumns = apiRef.current.state.columns.orderedFields.reduce((acc: any, key: string) => {
          if (params[key] !== false) {
            acc.push(key);
          }
          return acc;
        }, []);
        console.log("newColumns", newColumns);

        setTimeout(() => {
          apiRef.current.autosizeColumns({
            columns: newColumns,
            includeOutliers: true,
            includeHeaders: true,
            expand: true,
          });
        }, 1000);
      };

      return apiRef.current.subscribeEvent("columnVisibilityModelChange", handleColumnVisibilityModelChange);
    }, [apiRef]);
    // TODO: replace this with the new useAutosizeColumns hook

    useEffect(() => {
      setPaginationModel({ pageSize: 200, page: 0 });
      const quickFilterValues = props.searchText.split(" ").filter((word) => word !== "");
      apiRef.current.setQuickFilterValues(quickFilterValues);
    }, [props.searchText]);

    const co2MaterialTypes = useAllMaterials({
      co2MaterialLibrary: props.co2MaterialLibrary.co2MaterialLibraryId || "",
      search: props.searchText,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
      //sortBy: "name",
    });



    useEffect(() => {
      console.log(co2MaterialTypes.data);
      setRowCount(co2MaterialTypes.data?.co2MaterialType?.totalCount || 0);

      const newRows = (co2MaterialTypes.data?.co2MaterialType?.nodes || []).map((row: any) => {
        const source = row.sourceData ?? {};
        return {
          ...row,
          id: row.co2MaterialTypeId || row.externalId,
          compare: props.compareSelectModel[row.co2MaterialTypeId || row.externalId] === true,
          type: source.Type ?? "material", // for non-veracity sources, default to 'material'
          system: source.System ?? null,
          subsystem: source.Subsystem ?? null,
          reference_period: source.ReferencePeriod ?? null,
          attribute1: source.Attribute1 ?? null,
          attribute2: source.Attribute2 ?? null,
          attribute3: source.Attribute3 ?? null,
          massPerUnit: row.unit,
          comment: source.Comment ?? null,
          mapping_variable: source.MappingVariable ?? null,
          reference_multiplier: source.ReferenceMultiplier ?? null,
          location: source.Location ?? null,
        };
      });
      const filteredRows = props.showTabs ? newRows.filter((r) => r.type?.toLowerCase() === props.tabType.toLowerCase()) : newRows;

      setRows(filteredRows);
      // setRows(newRows);
      if ((co2MaterialTypes.data?.co2MaterialType?.nodes?.length || 0) > 100) {
        setPageSizeOptions([10, 25, 50, 100, co2MaterialTypes.data?.co2MaterialType?.nodes?.length || 0]);
        //setPaginationModel({ pageSize: data?.co2MaterialType?.nodes?.length, page: 0 });
      }

      const newColumns =
        co2MaterialTypes.data?.co2MaterialType?.nodes?.reduce((acc: any, node: any) => {
          const properties = Object.properties(node?.sourceData || {}, "sourceData");
          properties?.forEach((property: string) => {
            acc.add(property);
          });
          return acc;
        }, new Set<string>()) || new Set<string>();

      newColumns.forEach((field: string) => {
        columnVisibilityModel[field] = false;
        columns.push({
          field,
          headerName: field.substring(field.indexOf(".") + 1),
          flex: 1,
          hideable: true,
          hidden: true,
          minWidth: 100,
          valueGetter: (_value: any, row: any) => {
            // Need to access the value using the dot notation blah.blah.blah
            return _.get(row, field);
          },
        } as any);
      });

      configureColumns(appStore, columns);
      setColumns([...columns]);

      const prevState = localStorage.getItem(localStorageKey);
      if (restoreInitialState && prevState) {
        const savedState = JSON.parse(prevState);
        const currentState = apiRef.current.exportState();
        const newState = {
          ...currentState,
          ...savedState,
          sorting: { ...savedState.sorting, sortModel: sortModelRef.current },
          pagination: { ...savedState.pagination, paginationModel: paginationModelRef.current },
        };
        apiRef.current.restoreState(newState);
        setRestoreInitialState(false);
      } else {
        setColumnVisibilityModel(columnVisibilityModel);
      }

      console.log("columns", columns);
      console.log("rows", co2MaterialTypes.data?.co2MaterialType?.nodes);
    }, [co2MaterialTypes.data, props.tabType]);

    const handleToggleCompareSelectModel = (params: any) => {
      const checked = params.row.compare === true;
      if (props.onCompareSelectModelChange) {
        props.onCompareSelectModelChange(params.row.id, params.row.compare);
        apiRef.current.updateRows([{ id: params.row.id, compare: !checked }]);
        compareMaterialsStore.toggleCo2MaterialType(params.row);
      }
    };

    const getMaterialTotalCo2 = (row: any, metrics: any) => {
      const co2Phases = ["a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "c1", "c2", "c3", "c4", "d", "biogenic"];

      // Safely sum all CO2 values, treating null/undefined as 0
      const totalCo2Impact = co2Phases.reduce((sum, key) => {
        const value = row[key];
        return sum + (typeof value === "number" ? value : 0);
      }, 0);

      switch (row.unit) {
        case "m":
          return metrics.length.total * totalCo2Impact * 0.001;
        case "m2":
          return metrics.area.total * totalCo2Impact * 0.001;
        case "m3":
          return metrics.volume.total * totalCo2Impact * 0.001;
        case "kg":
          if (row.densityType === DensityType.Area) {
            return metrics.area.total * (row.density || 0) * totalCo2Impact * 0.001;
          }
          if (row.densityType === DensityType.Volume) {
            return metrics.volume.total * (row.density || 0) * totalCo2Impact * 0.001;
          }
          return 0;
        case "item":
          return metrics.quantity.total * totalCo2Impact * 0.001;
        default:
          return 0;
      }
    };

    useEffect(() => {
      let columnDefs: ExtendedGridColDef[];

      if (props.showTabs) {
        columnDefs = getTabColumns(props.tabType);
      } else {
        columnDefs = getDefaultColumns();
      }
      if (props.metrics !== undefined) {
        columnDefs.push({
          field: "co2eTotal",
          headerName: i18n.t("assign-material-dialog.columns.co2etotal"),
          flex: 1,
          minWidth: 200,
          type: "number",
          valueGetter: (_value: any, row: any) => {
            return getMaterialTotalCo2(row, props.metrics);
          },
          valueFormatter: (value: any) => {
            return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
          },
        });
      }

      const renderCompareCheckbox = (params: any) => {
        return <Checkbox icon={<CheckBoxOutlineBlankIcon />} checked={params.row.compare} onClick={() => handleToggleCompareSelectModel(params)} />;
      };

      if (props.compare) {
        columnDefs.push({
          field: "compare",
          headerName: i18n.t("assign-material-dialog.columns.compare"),
          flex: 1,
          minWidth: 100,
          headerAlign: "center",
          align: "center",
          renderCell: renderCompareCheckbox,
        });
      }

      configureColumns(appStore, columnDefs);
      setColumns(columnDefs);

      const visibilityModel = columnDefs.reduce((acc: any, col: any) => {
        if (col.hidden) acc[col.field] = false;
        return acc;
      }, {});
      setColumnVisibilityModel(visibilityModel);
    }, [props.tabType, props.showTabs]);

    const handlePaginationModelChange = (paginationModel: any) => {
      console.log("handlePaginationModelChange", paginationModel);
      setPaginationModel(paginationModel);
      /*
    co2MaterialTypes.refetch({
      co2MaterialLibrary: props.co2MaterialLibrary.co2MaterialLibraryId,
      search: props.searchText,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    });
    */
    };

    return (
      <DataGrid
        apiRef={apiRef}
        loading={co2MaterialTypes.isFetching}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        rows={rows}
        hideFooter={false}
        pagination={props.co2MaterialLibrary.isLazyLoading}
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={(newPaginationModel) => handlePaginationModelChange(newPaginationModel)}
        rowCount={rowCount}
        paginationMode="server"
        onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
          console.log("onRowSelectionModelChange", ids);
          setRowSelectionModel(ids);
          if (props.onRowSelectModelChange) {
            const selectedRow = rows.find((row: any) => row.co2MaterialTypeId === ids[0] || row.externalId === ids[0]);
            props.onRowSelectModelChange(props.co2MaterialLibrary, selectedRow);
          }
        }}
        onColumnVisibilityModelChange={(columnVisibilityModel: any) => {
          console.log("onColumnVisibilityModelChange", columnVisibilityModel);
          setColumnVisibilityModel(columnVisibilityModel);
        }}
        rowSelectionModel={rowSelectionModel}
        slots={{
          toolbar: CustomToolbarComponent,
        }}
        slotProps={{
          toolbar: {
            name: props.co2MaterialLibrary.name,
            showCompare: props.compare,
            onShowCompare: props.onShowCompare,
          },
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        initialState={{
          density: "compact",
          columns: {
            columnVisibilityModel: columns.reduce((acc: any, column: any) => {
              if (column.hidden) {
                acc[column.field] = false;
              }
              return acc;
            }, {}),
          },
          sorting: { sortModel },
        }}
      />
    );
  }),
);

const locationWrapOperator = (operator: GridFilterOperator) => {
  const getApplyFilterFn: GridFilterOperator["getApplyFilterFn"] = (filterItem, column) => {
    const innerFilterFn = operator.getApplyFilterFn(filterItem, column);
    if (!innerFilterFn) {
      return innerFilterFn;
    }

    return (value, row, col, apiRef) => {
      const valueOption = (col as any).valueOptions.find((option: any) => option.alpha3 === filterItem.value);
      if (valueOption && valueOption.type === "country") {
        const filterItemRegion = { ...filterItem, value: valueOption.region };
        const innerFilterFnByRegion = operator.getApplyFilterFn(filterItemRegion, column)!;

        return innerFilterFn(value, row, col, apiRef) || innerFilterFnByRegion(value, row, col, apiRef);
      }
      if (valueOption && valueOption.type === "region") {
        const regionOption = (col as any).valueOptions.find((option: any) => option.alpha3 === filterItem.value);
        const filterItemRegion = { ...filterItem, value: regionOption.region };
        const innerFilterFnByRegion = operator.getApplyFilterFn(filterItemRegion, column)!;
        const selectedOption = (col as any).valueOptions.find((option: any) => option.alpha3 === value);

        return innerFilterFn(value, row, col, apiRef) || innerFilterFnByRegion(selectedOption.region, row, col, apiRef);
      }
      return innerFilterFn(value, row, col, apiRef);
    };
  };

  return { ...operator, getApplyFilterFn };
};

const configureColumns = (appStore: AppStore, columns: ExtendedGridColDef[]) => {
  const system = columns.find((c) => c.field === "system") as any;
  if (system) {
    system.type = "singleSelect";
    system.valueOptions = transformMetadataToSystemOptions(appStore.metadataEntities);
    system.valueOptions.shift(); // remove the default "-- Select System --" option
    system.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    system.getOptionLabel = (option: any) => {
      return option.label;
    };
    system.getOptionValue = (option: any) => {
      return option.label;
    };
  }
  const subSystem = columns.find((c) => c.field === "subsystem") as any;
  if (subSystem) {
    subSystem.type = "singleSelect";
    subSystem.valueOptions = transformMetadataToSubSystemOptions(appStore.metadataEntities);
    subSystem.valueOptions.shift(); // remove the default "-- Select SubSystem --" option
    subSystem.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    subSystem.getOptionLabel = (option: any) => {
      return option.label;
    };
    subSystem.getOptionValue = (option: any) => {
      return option.label;
    };
  }
  const location = columns.find((c) => c.field === "location" || c.field === "sourceData.Location") as any;
  if (location) {
    location.type = "singleSelect";
    location.valueOptions = transformMetadataToLocationOptions(appStore.metadataEntities);
    location.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    location.getOptionLabel = (option: any) => {
      return option.label;
    };
    location.getOptionValue = (option: any) => {
      return option.alpha3;
    };
    const singleSelectColDef = getGridDefaultColumnTypes()["singleSelect"];
    location.filterOperators = singleSelectColDef.filterOperators?.map((operator: GridFilterOperator) => locationWrapOperator(operator));
  }
};

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    name: string;
    showCompare: boolean;
    onShowCompare?: () => void;
  }
}

const CustomToolbarComponent = (props: NonNullable<GridSlotsComponentsProps["toolbar"]>) => {
  const theme = useTheme();

  return (
    <GridToolbarContainer
      sx={{
        borderBottom: 1,
        backgroundColor: getBackgroundColor(theme),
        borderColor: getBorderColor(theme),
        fontWeight: "bold",
      }}
    >
      <Typography
        p={1}
        variant="body2"
        sx={{
          fontWeight: "bold",
        }}
      >
        {props.name}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{ disableToolbarButton: true }}
        printOptions={{ disableToolbarButton: true }}
        excelOptions={{
          allColumns: true,
        }}
      />
      {props.showCompare && (
        <GridToolbarCompareButton
          slotProps={{
            button: {
              onClick: () => {
                if (props.onShowCompare) {
                  props.onShowCompare();
                }
              },
            },
          }}
        />
      )}
    </GridToolbarContainer>
  );
};

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default MaterialSelectGrid;
